import React, { Dispatch, SetStateAction, useState } from 'react';
import { ProgressBar } from 'react-bootstrap';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { ExpenseDA } from '../../services/admin/expense.service';
import { Company } from '../../models/company.model';
import { Error } from '../../models/error.model';
import { Expense } from '../../models/expense.model';
import { modalHide } from '../../utils/modal.util';
import { errorUploadFiles, uploadPdf } from '../../scripts/file.load.script';
import { navBarShow } from '../../utils/nav.bar.util';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';

import imgFilePdf from '../../assets/themes/icons/archive-pdf-icon.svg'

export interface AppModalExpenseCreateDraftProps {
  setAsideMinimize: Dispatch<SetStateAction<string>>,
  companyForUser: Company | null,
  setExpenseDraft: Dispatch<SetStateAction<Expense | null>>
};

let errorResponse: Error, expenseResponse: Expense;

const AppModalExpenseCreateDraft: React.FunctionComponent<AppModalExpenseCreateDraftProps> = ({setAsideMinimize, companyForUser, setExpenseDraft}) => {
  const {lang} = StateLanguage()

  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState({view: false, message: ''})

  const submitExpenseCreateDraft = async (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setUploading(true)
    setProgress(0)
    setError({view: false, message: ''})

    if (!companyForUser) {
      setError({view: true, message: lang.labels.error})
      setUploading(false)
      return
    } else {
      setProgress(5)
    }

    let companyData = {uuid: companyForUser.uuid, name: companyForUser.name, logo: companyForUser.logo, address: companyForUser.address, phone: companyForUser.phone || '', email: companyForUser.email || '', website: companyForUser.website || '', vat_number: companyForUser.vat_number || ''}
    let listValid: {file: boolean, message: string}[] = []
    let listValue: {name: string, file: any}[] = []
    let archives: File[] = Array.from(event.target.files || [])
    let step: number = 35 / archives.length
    let progress: number = 5

    for (let item of archives.reverse()) {
      let archive = await uploadPdf(lang, item)
      let target = Math.min(progress + step, 35)

      listValue.push({name: archive.value.name, file: archive.value.file})

      if (archive.valid.file) {
        listValid.unshift(archive.valid)
      } else {
        listValid.push(archive.valid)
      }

      while (progress < target) {
        progress += 1
        setProgress(Math.min(progress, 50))
        await new Promise(res => setTimeout(res, 10))
      }
    }

    if (!listValid.every((item) => (item.file))) {
      setUploading(false)
      setError({ view: true, message: errorUploadFiles(lang, listValid) })
      return
    }

    const animate = () => {
      return new Promise<void>((resolve) => {
        const interval = setInterval(() => {
          progress += 1
          if (progress >= 95) {
            clearInterval(interval)
            progress = 95
            resolve()
          }
          setProgress(progress)
        }, 20)
      })
    }

    const service = ExpenseDA.postExpenseDraft(companyForUser.id, listValue, companyData).then( (response) => {
      if (response.status === 201) {
        expenseResponse = response.data

        const interval = setInterval(() => {
          progress += 1
          if (progress >= 100) {
            clearInterval(interval)
            setProgress(100)

            setTimeout(() => {
              setAsideMinimize("on")
              navBarShow('button-navbar-aside-minimize')
              modalHide('modal-expense-create-draft')
              setExpenseDraft(expenseResponse)

              setTimeout(() => {
                restartModal()
              }, 200)
            }, 500)
          } else {
            setProgress(progress)
          }
        }, 20)
      } else {
        errorResponse = response.data
        setError({view: true, message: getError(errorResponse.code, lang.code, errorResponse.message)})
        setUploading(false)
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })

    await Promise.all([animate, service])
  }

  const executeHideModalProviderInvoicePrevious = () => {
    modalHide('modal-expense-create-draft')
    setExpenseDraft(null)

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    setUploading(false)
    setProgress(0)
    setError({view: false, message: ''})
  }

  return (
    <div id="modal-expense-create-draft" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.beforeCreatingExpense}</h2>
          </div>
          <div className="modal-body">
            <div className="form">
              <div className="text-center fs-6 mb-8">
                <h5 className="mb-5">{lang.labels.wouldYouLikeUploadPdfFilesToRegistrationNnewInvoice}</h5>
                <label>{lang.labels.whenYouUploadPdfFilesTheyArePlacedReferenceSupplieIinvoiceCreationform}</label>
              </div>
              <div className="fv-row mb-2">
                <div className={`dropzone ${error.view ? "bg-light-warning border-warning": "bg-light-success border-success"} position-relative align-content-center w-100 p-5`}>
                  <input className="position-absolute top-0 start-0 w-100 h-100 opacity-0 cursor-pointer" multiple type="file" name="logo" accept=".pdf" onChange={submitExpenseCreateDraft} />
                  { uploading
                    ?
                    <div className="d-flex align-items-center">
                      <div className="w-100">
                        <div className="text-start fw-bold mb-2">{lang.labels.loading}</div>
                        <ProgressBar className="bg-secondary h-25px" animated now={progress} label={`${Math.round(progress)}%`} />
                      </div>
                    </div>
                    :
                    <div className="d-flex align-items-center">
                      <img src={imgFilePdf} className="mw-100 mh-200px" alt={lang.labels.pdf} />
                      <div className="d-flex flex-column ms-3">
                        <span className="fs-5 fw-bold text-start">{lang.labels.dropFilesHereOrClickUpload}</span>
                        <span className="form-text text-justify text-muted">{lang.labels.fileFormatMaximum10MBPerFile}</span>
                      </div>
                    </div>
                  }
                </div>
              </div>
              <div className={`fv-plugins-message-container invalid-feedback ${!error.view && "d-none"}`}>
                <div data-field="document" data-validator="file">{error.message}</div>
              </div>
            </div>
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-primary" type="reset" disabled={uploading} onClick={executeHideModalProviderInvoicePrevious}>{lang.labels.noContinueForm}</button>
          </div>
        </div>
      </div>
    </div>
  )
};

export default AppModalExpenseCreateDraft;
